// The payload `add_to_cart` and `remove_from_cart` share. The two events differ only in
// name and in which direction the tickets moved, so one builder serves both — and serves
// both screens that can move a ticket: `TicketRow` on an ordinary event page, and
// `SeatsioPicker` on a seated one. Shared rather than copied so "the seated path sends the
// same shape" stays true past the next edit to either side.
//
// See packages/checkout/doc/claude/analytics.md for the event inventory.

import type { Checkout } from '@vp/common';
import type { TrackingItem } from './trackingItems';

/**
 * What a cart event needs to know about the ticket type that moved.
 *
 * `id` alone is enough. The picker resolves its blueprint out of the store by id, so the
 * lookup is typed as possibly missing; a row carrying only `item_id` still gives the
 * funnel its join key, which beats sending no hit at all.
 */
export type CartEventTicket =
  Pick<Checkout.TicketBlueprint.CheckoutTicketBlueprint, 'id'>
  & Partial<Pick<Checkout.TicketBlueprint.CheckoutTicketBlueprint, 'name' | 'breakdown'>>;

export type CartEventPayload = {
  currency: string;
  value: number;
  items: TrackingItem[];
};

/**
 * One movement of one ticket type.
 *
 * `value` is the worth of that movement — what was added or removed — rather than the
 * cart total, which is what GA4 (and Meta, reading the same key) expect on these two.
 * A blueprint with no price values at `0` rather than dropping the key: that is what the
 * non-seated path has always sent, and changing it is out of scope for sharing it.
 *
 * `currency` arrives **already normalised**. doc/claude/analytics.md § "Payload currency"
 * splits the two resolvers by what the call site holds — `trackingCurrency(code)` for a
 * screen holding the string, `eventTrackingCurrency(event)` for one holding the event —
 * and normalising in here would collapse that split.
 */
export const cartEventPayload = (
  ticket: CartEventTicket,
  quantity: number,
  currency: string,
): CartEventPayload => {
  const price = ticket.breakdown?.displayPrice;

  return {
    currency,
    value: (price ?? 0) * quantity,
    items: [
      {
        item_id: ticket.id.toString(),
        item_name: ticket.name,
        quantity,
        price,
      },
    ],
  };
};
